<template>
  <section class="spv-empty">
    <div class="spv-empty__icon" v-if="spvType === 'requests' || spvType === 'spvs' || spvType === 'default'">
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
        <polyline points="14 2 14 8 20 8"></polyline>
        <line x1="16" y1="13" x2="8" y2="13"></line>
        <line x1="16" y1="17" x2="8" y2="17"></line>
        <polyline points="10 9 9 9 8 9"></polyline>
      </svg>
    </div>
    <h2 class="spv-empty__title">{{ spvHeading }}</h2>
    <p class="spv-empty__text">{{ spvText }}</p>
    <button class="spv-empty__btn" @click="showOptions = true">
      Create SPV
    </button>
    <button
      v-if="spvType === 'default'"
      class="spv-empty__link"
      @click="showSpvModal = true"
    >
      What is an SPV?
    </button>
    <SpvModal
      v-if="showSpvModal"
      @close-spv-modal="showSpvModal = false"
      @show-spv-options="switchModal('spvOptions')"
    />
    <SpvOptionsModal
      v-if="showOptions"
      @close-spv-options="showOptions = false"
    />
  </section>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import SpvModal from "@/components/spv/SpvModal.vue";
import SpvOptionsModal from "@/components/spv/SpvOptionsModal.vue";

const props = defineProps({
  spvType: {
    type: String,
    required: false,
    default: () => "default",
  },
  emptyType: {
    type: String,
    required: false,
    default: () => "default",
  },
});

const spvType = computed(() => props.spvType);
const emptyType = computed(() => props.emptyType);
const showSpvModal = ref(false);
const showOptions = ref(false);

const spvHeading = computed(() => {
  let heading = null;
  switch (spvType.value) {
    case "requests":
      heading = getRequestCopy().heading;
      break;
    case "spvs":
      heading = getDocsCopy().heading;
      break;
    default:
      heading = "You do not have an SPV yet";
      break;
  }
  return heading;
});

const spvText = computed(() => {
  let text = null;
  switch (spvType.value) {
    case "requests":
      text = getRequestCopy().text;
      break;
    case "spvs":
      text = getDocsCopy().text;
      break;
    default:
      text = "Create an SPV that can be linked to any fund raising deal you have created.";
      break;
  }
  return text;
});

const getRequestCopy = () => {
  let heading = null;
  let text = null;
  switch (emptyType.value) {
    case "search":
      heading = "Not found";
      text = "You do not have an SPV with this name";
      break;
    case "filter":
      heading = "Not found";
      text = "You do not have an SPV with this status";
      break;
    default:
      heading = "You have not made any SPV requests yet";
      text = "Create an SPV that can be linked to any fund raising deal you have created.";
      break;
  }
  return { heading, text };
};

const getDocsCopy = () => {
  let heading = null;
  let text = null;
  switch (emptyType.value) {
    case "search":
      heading = "Not found";
      text = "You do not have an SPV Document with this name";
      break;
    default:
      heading = "You do not have an SPV yet";
      text = "Create an SPV that can be linked to any fund raising deal you have created.";
      break;
  }
  return { heading, text };
};

const switchModal = (modal) => {
  switch (modal) {
    case "spvOptions":
      showOptions.value = true;
      showSpvModal.value = false;
      break;
  }
};

watch(
  () => props.emptyType,
  () => {
    if (spvType.value === "requests") {
      getRequestCopy();
    } else {
      getDocsCopy();
    }
  }
);
</script>

<style lang="scss" scoped>
// Design System Variables
$text-primary: #0F172A;
$text-secondary: #475569;
$text-muted: #64748B;
$bg-card: #FFFFFF;
$bg-subtle: #F1F5F9;
$border-color: #E2E8F0;
$accent-primary: #4D84FF;
$btn-primary: #001240;
$btn-primary-hover: #06206B;

.spv-empty {
  background: $bg-card;
  border: 1px solid $border-color;
  border-radius: 10px;
  padding: 48px 32px;
  max-width: 818px;
  margin: 0 auto;
  text-align: center;

  &__icon {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    background: $bg-subtle;
    color: $text-muted;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
  }

  &__title {
    font-size: 18px;
    font-weight: 600;
    color: $text-primary;
    margin: 0 0 8px;
  }

  &__text {
    font-size: 14px;
    color: $text-muted;
    max-width: 358px;
    margin: 0 auto 24px;
    line-height: 1.5;
  }

  &__btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 12px 32px;
    background: $btn-primary;
    color: white;
    border: none;
    border-radius: 7px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s ease;

    &:hover {
      background: $btn-primary-hover;
    }
  }

  &__link {
    display: block;
    margin-top: 20px;
    font-size: 14px;
    font-weight: 500;
    color: $accent-primary;
    text-decoration: underline;
    background: none;
    border: none;
    cursor: pointer;
    transition: color 0.2s ease;

    &:hover {
      color: darken($accent-primary, 10%);
    }
  }
}
</style>
